import { Box, chakra, IconButton, useTheme } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { ComponentProps } from 'react';

type Props = { color?: string; duration?: number | null } & ComponentProps<typeof IconButton>;

const DEFAULT_DURATION = 5000; // milliseconds
const RADIUS = 12; // pixels
const CIRCUMFERENCE = Math.ceil(2 * Math.PI * RADIUS);

const CIRCLE_ANIMATION = keyframes`
  from {
    stroke-dasharray: 0 ${CIRCUMFERENCE};
    opacity: 0.33;
  }
  to {
    stroke-dasharray: ${CIRCUMFERENCE} ${CIRCUMFERENCE};
    opacity: 0.8;
  }
`;

const IconWrapper = ({ icon, color, duration }: Pick<Props, 'icon' | 'color' | 'duration'>) => (
  <Box
    position="absolute"
    top={0}
    left={0}
    width="full"
    height="full"
    textColor={color}
    sx={{
      svg: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: 'full',
        height: 'full',
        transformOrigin: 'center',
        transform: 'translate(-50%,-50%) rotate(-90deg)',
      },
      'svg ~ svg': {
        width: '40%',
        height: '40%',
        transform: 'translate(-50%,-50%)',
      },
    }}
  >
    <svg viewBox={`0 0 ${RADIUS * 2 + 1} ${RADIUS * 2 + 1}`}>
      <chakra.circle
        r="12"
        cx="50%"
        cy="50%"
        stroke={color}
        fill="none"
        opacity={0.63}
        strokeWidth={1}
        animation={
          !!duration || typeof duration === 'undefined'
            ? `${CIRCLE_ANIMATION} linear ${duration || DEFAULT_DURATION}ms both`
            : ''
        }
      />
    </svg>

    {icon}
  </Box>
);

export const TimeoutButton = ({ icon, color, duration, ...props }: Props) => {
  const theme = useTheme();
  const resolvedColor = color ? theme.colors[color] || color : theme.colors.black;
  return (
    <IconButton
      {...props}
      variant="icon"
      icon={<IconWrapper icon={icon} color={resolvedColor} duration={duration} />}
    />
  );
};
